import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Image as ImageIcon,
  MapPin,
  Waves,
  ShieldCheck,
  Building2,
} from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { projectCards, gallery, assets } from "../data/siteData";

const projectHighlights = [
  "Urban and public water access deployments",
  "Solar-ready and remote infrastructure installations",
  "Treatment-linked field systems with strong usability",
  "Branded, visible, and trust-building public assets",
];

const stats = [
  {
    value: "Field-Ready",
    label: "Built for real operating conditions and day-to-day use",
    icon: ShieldCheck,
  },
  {
    value: "Multi-Site",
    label: "Suitable for public, institutional, and remote deployments",
    icon: Building2,
  },
  {
    value: "Visible",
    label: "Designed to inspire trust through strong physical presence",
    icon: MapPin,
  },
  {
    value: "Integrated",
    label: "Engineered with usability, treatment, and infrastructure in mind",
    icon: Waves,
  },
];

function CinematicProjectSection({
  title,
  desc,
  image,
  reverse = false,
  index,
}) {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0">
        <img
          src={image}
          alt={title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/45" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/45 to-slate-950/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-10 lg:py-32">
        <div
          className={`grid items-center gap-10 lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""
            }`}
        >
          <motion.div
            initial={{ opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true, amount: 0.25 }}
            className="relative z-10 max-w-2xl"
          >
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/75 backdrop-blur-md">
              Featured Project 0{index + 1}
            </div>

            <h2 className="mt-6 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl">
              {title}
            </h2>

            <p className="mt-6 max-w-xl text-base leading-8 text-white/80 sm:text-lg">
              {desc}
            </p>

            <div className="mt-8 h-px w-24 bg-white/25" />

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-200"
              >
                Discuss Similar Project
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>

              <Link
                to="/solutions"
                className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.06] px-6 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-white/[0.12]"
              >
                Explore Solutions
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.75 }}
            viewport={{ once: true, amount: 0.2 }}
            className="relative"
          >
            <div className="ml-auto max-w-xl rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-[0_30px_100px_rgba(0,0,0,0.35)] backdrop-blur-xl">
              <div className="overflow-hidden rounded-[1.5rem] border border-white/10">
                <img
                  src={image}
                  alt={title}
                  className="h-[300px] w-full object-cover sm:h-[360px] lg:h-[420px]"
                />
              </div>

              <div className="mt-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">
                    Deployment Profile
                  </p>
                  <p className="mt-2 text-lg font-semibold text-white">
                    Real-world water infrastructure
                  </p>
                </div>

                <div className="hidden rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-right sm:block">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/45">
                    Status
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white/90">
                    Installed / Field Proven
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function ProjectsPage() {
  return (
    <>
      <SEO
        title="Water Treatment Projects in Kolkata & West Bengal | Hydrotec Portfolio"
        description="Explore Hydrotec Solutions' water treatment, pump, water ATM, and monitoring projects across Kolkata, Newtown, Rajarhat, and West Bengal. Real deployments for communities, institutions, and industry."
        keywords="water treatment projects Kolkata, water infrastructure projects West Bengal, water ATM projects Newtown, Hydrotec projects Rajarhat, water project portfolio Kolkata"
        canonical="https://hydroteclimited.com/projects"
      />
      <PageHero
        title="Projects that prove real deployment capability."
        text="From urban refill stations to solar-powered kiosks and treatment-linked installations, Hydrotec projects show how water infrastructure performs in real environments."
        image={assets.projects}
        primaryText="Start a Project"
        primaryLink="/contact"
        secondaryText="Explore Solutions"
        secondaryLink="/solutions"
      />

      <section className="relative overflow-hidden bg-white text-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(6,182,212,0.08),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(15,23,42,0.08),transparent_30%)]" />

        <div className="relative mx-auto grid max-w-7xl gap-14 px-6 py-24 sm:px-8 lg:grid-cols-[1fr_1fr] lg:px-10 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            viewport={{ once: true, amount: 0.25 }}
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              Project Portfolio
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Sustainable Infrastructure designed to perform in the field.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            viewport={{ once: true, amount: 0.25 }}
          >
            <p className="text-lg leading-8 text-slate-600">
              Hydrotec deployments are shaped for real operating conditions
              across public environments, institutional sites, community access
              points, and remote infrastructure settings. Each project reflects
              a balance of engineering, usability, durability, and visual trust.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {projectHighlights.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-2xl border border-emerald-400 bg-white px-4 py-4 shadow-sm"    >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700" />
                  <span className="text-sm leading-7 text-slate-700">{item}</span>
                </div>
              ))}
            </div></motion.div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-px px-6 py-0 sm:px-8 lg:grid-cols-4 lg:px-10 col
        or-[#5d6c97]">
          {stats.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.value}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                viewport={{ once: true, amount: 0.2 }}
                className="border-white/10 px-0 py-0 lg:border-l lg:first:border-l-0"
              >
                <div className="h-full bg-white/[0.03] px-6 py-10 transition hover:bg-white/[0.05]">
                  <Icon className="h-6 w-6 text-cyan-400" />
                  <p className="mt-5 text-2xl font-semibold tracking-tight">
                    {item.value}
                  </p>
                  <p className="mt-2 max-w-xs text-sm leading-7 text-white/65">
                    {item.label}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {projectCards.map((item, index) => (
        <CinematicProjectSection
          key={item.title}
          title={item.title}
          desc={item.desc}
          image={item.image}
          reverse={index % 2 === 1}
          index={index}
        />
      ))}

      <section className="bg-white text-slate-950">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            viewport={{ once: true, amount: 0.25 }}
            className="relative overflow-hidden rounded-[2.25rem] border border-slate-200 bg-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.12)]"
          >
            <img
              src={assets.waterAtmCity}
              alt="Hydrotec urban deployment"
              className="h-full min-h-[440px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 rounded-[1.5rem] border border-white/20 bg-white/15 p-5 backdrop-blur-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
                On-Site Presence
              </p>
              <p className="mt-2 text-xl font-semibold text-white sm:text-2xl">
                Physical infrastructure that users can see, approach, and trust.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            viewport={{ once: true, amount: 0.25 }}
            className="self-center"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              On-Ground Experience
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Designed to be visible, usable, and trusted.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              The strongest infrastructure does more than function. It builds
              confidence in the people who use it. Hydrotec projects aim to
              create that trust through quality design, strong system
              integration, accessible operation, and dependable performance.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6">
                <MapPin className="h-6 w-6 text-cyan-700" />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                  Locations
                </p>
                <p className="mt-2 text-lg font-semibold text-slate-900">
                  Urban · Rural · Remote
                </p>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6">
                <ImageIcon className="h-6 w-6 text-cyan-700" />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                  Identity
                </p>
                <p className="mt-2 text-lg font-semibold text-slate-900">
                  Visible · Branded · Trusted
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-slate-200 bg-slate-100/80">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(6,182,212,0.08),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(15,23,42,0.07),transparent_32%)]" />

        <div className="relative mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-10 lg:py-28">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              Visual Gallery
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Visual proof of real-world implementation.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Installation photos, treatment-linked field systems, service-ready
              equipment, and branded infrastructure snapshots from Hydrotec
              deployments.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3">
            {gallery.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.28, delay: index * 0.05 }}
                viewport={{ once: true, amount: 0.18 }}
                className="group overflow-hidden rounded-[2rem] border border-white/60 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.08)]"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-80 w-full object-cover transition duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="rounded-2xl border border-white/15 bg-white/15 p-4 backdrop-blur-xl">
                      <div className="inline-flex items-center gap-2 text-white">
                        <ImageIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(6,182,212,0.15),transparent_22%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_26%)]" />

        <div className="relative mx-auto max-w-5xl px-6 py-24 text-center sm:px-8 lg:px-10 lg:py-32">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-white/55">
            Build With Hydrotec
          </p>

          <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
            Ready for your next installation?
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-white/72">
            Talk to Hydrotec about safe water access systems, treatment-linked
            deployments, pump-supported infrastructure, and digitally visible
            field-ready water solutions.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-200"
            >
              Start a Project <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>

            <Link
              to="/solutions"
              className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.06] px-6 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-white/[0.12]"
            >
              Explore Solutions
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}